import React, { ReactNode, useId, useState } from 'react';

import { TabContext } from '../context/TabContext';

type TabsProps = {
  children: ReactNode;
  /**
   * Tab which should be selected initially, for uncontrolled usage. 0-based.
   */
  initialSelected?: number;
  /**
   * Controlled selected tab index. 0-based. When provided, `Tabs` becomes
   * controlled: the parent owns the selection and must update it via
   * `onSelectedChange`.
   */
  selected?: number;
  /**
   * Called with the index of a tab when it is selected. Required to react to
   * tab clicks in controlled usage; also useful as a change listener in
   * uncontrolled usage.
   */
  onSelectedChange?: (index: number) => void;
};

/**
 * Tabs make it easy to switch between different views.
 * The naming follows WAI-ARA specs: https://w3c.github.io/aria-practices/#tabpanel.
 * Keyboard navigation with `Arrow-Left`, `Arrow-Right` available.
 *
 * Works both uncontrolled (`initialSelected`) and controlled (`selected` +
 * `onSelectedChange`).
 */
const Tabs = ({
  children,
  initialSelected,
  selected,
  onSelectedChange,
}: TabsProps) => {
  const isControlled = selected !== undefined;
  const [internalSelected, setInternalSelected] = useState(initialSelected);
  const selectedTab = isControlled ? selected : internalSelected;

  const setSelectedTab = (tab: number) => {
    if (!isControlled) {
      setInternalSelected(tab);
    }
    onSelectedChange?.(tab);
  };

  const idPrefix = useId();
  const [focusedTab, setFocusedTab] = useState<number | undefined>();
  // Closure to keep track of `TabPanel` count.
  // Referenced further down within React.Children.map()
  let tabPanelCounter = 0;

  return (
    <TabContext.Provider
      value={{
        idPrefix,
        selectedTab,
        setSelectedTab,
        focusedTab,
        setFocusedTab,
      }}
    >
      {React.Children.map<ReactNode, ReactNode>(children, child => {
        if (React.isValidElement(child)) {
          // Necessary to access displayName.
          // Pattern documented here https://github.com/rmolinamir/typescript-cheatsheet#usememo
          // Variation for pattern here: https://stackoverflow.com/questions/70250412/property-docgeninfo-does-not-exist-on-type-string-for-react-child
          const RFC_Child: React.FunctionComponent =
            child.type as React.FunctionComponent;
          const { displayName } = RFC_Child;

          if (displayName === 'TabPanel') {
            const clonedElement = React.cloneElement(child, {
              currentIndex: tabPanelCounter,
            } as React.HTMLAttributes<HTMLElement>);

            tabPanelCounter += 1;

            return clonedElement;
          }
          return child;
        }
      })}
    </TabContext.Provider>
  );
};

export { Tabs };
